import * as React from 'react';
import {
  Bell,
  Gift,
  MoreHorizontal,
  Palmtree,
  Plus,
  Shield,
  UserMinus,
  Users as UsersIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ConfirmDialog } from '@/components/app/ConfirmDialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { NotificationCategory } from '@/features/notifications/types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useAdjustXp,
  useBypassPrerequisite,
  useForceEnqueue,
  useGrantAccessory,
  useSendUserNotification,
  useSoftDeleteUser,
  useToggleHoliday,
} from '../hooks';
import type { UserDetail } from '../types';

interface Props {
  userDetail: UserDetail;
}

type DialogKind =
  | 'xp'
  | 'holiday'
  | 'accessory'
  | 'notify'
  | 'softDelete'
  | 'bypass'
  | 'enqueue'
  | null;


export function UserActionsMenu({ userDetail }: Props) {
  const userId = userDetail.user.userId;
  const [dialog, setDialog] = React.useState<DialogKind>(null);
  const close = () => setDialog(null);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <MoreHorizontal className="mr-1 h-4 w-4" /> Actions
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuLabel>Support</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => setDialog('xp')}>
            <Plus className="mr-2 h-4 w-4" /> Adjust XP
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setDialog('holiday')}>
            <Palmtree className="mr-2 h-4 w-4" /> Toggle holiday
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setDialog('accessory')}>
            <Gift className="mr-2 h-4 w-4" /> Grant/revoke accessory
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setDialog('notify')}>
            <Bell className="mr-2 h-4 w-4" /> Send notification
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Matching</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => setDialog('bypass')}>
            <Shield className="mr-2 h-4 w-4" /> Bypass prerequisite
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setDialog('enqueue')}>
            <UsersIcon className="mr-2 h-4 w-4" /> Force enqueue
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-destructive">Destructive</DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => setDialog('softDelete')}
            className="text-destructive focus:text-destructive"
          >
            <UserMinus className="mr-2 h-4 w-4" /> Soft-delete account
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {dialog === 'xp' && (
        <AdjustXpDialog userId={userId} open onOpenChange={(o) => !o && close()} />
      )}
      {dialog === 'holiday' && (
        <ToggleHolidayDialog
          userId={userId}
          onHoliday={!!userDetail.user.isOnHoliday}
          open
          onOpenChange={(o) => !o && close()}
        />
      )}
      {dialog === 'accessory' && (
        <GrantAccessoryDialog userId={userId} open onOpenChange={(o) => !o && close()} />
      )}
      {dialog === 'notify' && (
        <SendNotificationDialog userId={userId} open onOpenChange={(o) => !o && close()} />
      )}
      {dialog === 'bypass' && (
        <BypassPrerequisiteDialog userId={userId} open onOpenChange={(o) => !o && close()} />
      )}
      {dialog === 'enqueue' && (
        <ForceEnqueueDialog userId={userId} open onOpenChange={(o) => !o && close()} />
      )}
      {dialog === 'softDelete' && (
        <SoftDeleteDialog
          userId={userId}
          email={userDetail.user.email}
          open
          onOpenChange={(o) => !o && close()}
        />
      )}
    </>
  );
}

// ── Individual action dialogs ────────────────────────────────────

function AdjustXpDialog({
  userId,
  open,
  onOpenChange,
}: {
  userId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [delta, setDelta] = React.useState('');
  const mutation = useAdjustXp(userId);

  const parsedDelta = Number.parseInt(delta, 10);
  const deltaValid = !Number.isNaN(parsedDelta) && parsedDelta !== 0;

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Adjust XP balance"
      description="Positive values credit the user; negative values debit."
      body={
        <div className="space-y-1.5">
          <Label htmlFor="xp-delta">Delta</Label>
          <Input
            id="xp-delta"
            type="number"
            inputMode="numeric"
            value={delta}
            onChange={(e) => setDelta(e.target.value)}
            placeholder="e.g. 100 or -50"
          />
        </div>
      }
      confirmLabel="Adjust XP"
      submitting={mutation.isPending}
      onConfirm={async ({ reason }) => {
        if (!deltaValid) return;
        await mutation.mutateAsync({ delta: parsedDelta, reason });
        onOpenChange(false);
      }}
    />
  );
}

function ToggleHolidayDialog({
  userId,
  onHoliday,
  open,
  onOpenChange,
}: {
  userId: string;
  onHoliday: boolean;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [until, setUntil] = React.useState('');
  const mutation = useToggleHoliday(userId);

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={onHoliday ? 'Clear holiday mode?' : 'Enable holiday mode?'}
      description={
        onHoliday
          ? 'Holiday mode suppresses streak breaks and pauses reminders. Clearing it re-enables both.'
          : 'Holiday mode pauses streak counting and suppresses reminders. Optional until-date.'
      }
      body={
        !onHoliday ? (
          <div className="space-y-1.5">
            <Label htmlFor="holiday-until">Holiday until (optional)</Label>
            <Input
              id="holiday-until"
              type="datetime-local"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
            />
          </div>
        ) : null
      }
      confirmLabel={onHoliday ? 'Clear holiday' : 'Enable holiday'}
      submitting={mutation.isPending}
      onConfirm={async () => {
        await mutation.mutateAsync({
          enable: !onHoliday,
          until: !onHoliday && until ? new Date(until).toISOString() : undefined,
        });
        onOpenChange(false);
      }}
    />
  );
}

function GrantAccessoryDialog({
  userId,
  open,
  onOpenChange,
}: {
  userId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [accessoryId, setAccessoryId] = React.useState('');
  const [action, setAction] = React.useState<'grant' | 'revoke'>('grant');
  const mutation = useGrantAccessory(userId);

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Grant or revoke accessory"
      description="Changes the user's ownedAccessoryIds. Grants are auto-claimed; revokes remove ownership."
      body={
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="accessory-id">Accessory ID</Label>
            <Input
              id="accessory-id"
              value={accessoryId}
              onChange={(e) => setAccessoryId(e.target.value)}
              placeholder="e.g. streakBadge_10"
              />
          </div>
          <div className="space-y-1.5">
            <Label>Action</Label>
            <Select value={action} onValueChange={(v) => setAction(v as 'grant' | 'revoke')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="grant">Grant</SelectItem>
                <SelectItem value="revoke">Revoke</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      }
      confirmLabel={action === 'grant' ? 'Grant' : 'Revoke'}
      destructive={action === 'revoke'}
      submitting={mutation.isPending}
      onConfirm={async () => {
        if (!accessoryId.trim()) return;
        await mutation.mutateAsync({ accessoryId: accessoryId.trim(), action });
        onOpenChange(false);
      }}
    />
  );
}

function SendNotificationDialog({
  userId,
  open,
  onOpenChange,
}: {
  userId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [title, setTitle] = React.useState('');
  const [body, setBody] = React.useState('');
  const [type, setType] = React.useState('admin_ping');
  const [category, setCategory] = React.useState<NotificationCategory>(NotificationCategory.SYSTEM);
  const mutation = useSendUserNotification(userId);

  const canSubmit = title.trim().length > 0 && body.trim().length > 0 && type.trim().length > 0;

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Send notification to user"
      description="Goes through the Decision Brain like any other notification — caps and quiet hours still apply."
      reasonRequired={false}
      body={
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="notif-title">Title</Label>
            <Input id="notif-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={100} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="notif-body">Body</Label>
            <Textarea id="notif-body" value={body} onChange={(e) => setBody(e.target.value)} maxLength={500} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="notif-type">Type</Label>
              <Input id="notif-type" value={type} onChange={(e) => setType(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select
                value={category}
                onValueChange={(v) => setCategory(v as NotificationCategory)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(NotificationCategory).map(([key, value]) => (
                    <SelectItem key={key} value={value}>
                      {key}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      }
      confirmLabel="Send"
      submitting={mutation.isPending}
      onConfirm={async () => {
        if (!canSubmit) return;
        await mutation.mutateAsync({
          title: title.trim(),
          body: body.trim(),
          notificationType: type.trim(),
          category,
        });
        onOpenChange(false);
      }}
    />
  );
}

function BypassPrerequisiteDialog({
  userId,
  open,
  onOpenChange,
}: {
  userId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const mutation = useBypassPrerequisite(userId);
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Bypass matching prerequisite?"
      description="Skips the first-goal / active-task prerequisite gate. Use sparingly."
      confirmLabel="Bypass"
      submitting={mutation.isPending}
      onConfirm={async ({ reason }) => {
        await mutation.mutateAsync({ reason });
        onOpenChange(false);
      }}
    />
  );
}

function ForceEnqueueDialog({
  userId,
  open,
  onOpenChange,
}: {
  userId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [categories, setCategories] = React.useState('');
  const mutation = useForceEnqueue(userId);

  const parsed = categories
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Force enqueue for matching?"
      description="Pushes the user into the matching queue with the given goal categories."
      reasonRequired={false}
      body={
        <div className="space-y-1.5">
          <Label htmlFor="enqueue-cats">Goal categories (comma-separated)</Label>
          <Input
            id="enqueue-cats"
            value={categories}
            onChange={(e) => setCategories(e.target.value)}
            placeholder="exercise, productivity"
          />
          <p className="text-xs text-muted-foreground">
            Must match VALID_GOAL_CATEGORY_KEYS in @repo/back-config. 1–10 entries.
          </p>
        </div>
      }
      confirmLabel="Enqueue"
      submitting={mutation.isPending}
      onConfirm={async () => {
        if (parsed.length < 1 || parsed.length > 10) return;
        await mutation.mutateAsync({ goalCategories: parsed });
        onOpenChange(false);
      }}
    />
  );
}

function SoftDeleteDialog({
  userId,
  email,
  open,
  onOpenChange,
}: {
  userId: string;
  email: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const mutation = useSoftDeleteUser(userId);
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Soft-delete user account?"
      description="The user will be deactivated and removed from their team. Data is retained for audit."
      destructive
      typedConfirmation={email}
      confirmLabel="Soft-delete"
      submitting={mutation.isPending}
      onConfirm={async ({ reason }) => {
        await mutation.mutateAsync({ reason });
        onOpenChange(false);
      }}
    />
  );
}

